import Grid from "@material-ui/core/Grid";
import LinearProgress from "@material-ui/core/LinearProgress";
import DoneIcon from "@material-ui/icons/Done";
import CloseIcon from "@material-ui/icons/Close";
import React, { FC, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useHistory, useParams } from "react-router";
import { routesPaths } from "../../../Routes";
import { useGetExamSessionCandidatesQuery } from "../../../api/rtk/ExamSessionCandidateApi";
import { useSignupMutation } from "../../../api/rtk/SignUpApi";
import { ModuleForSignupViewModel } from "../../../model/ExamSignup/ExamSessionForSignupViewModel";
import { GetTranslatedValueIntl } from "../../../model/Shared/TranslatedValue";
import {
  CandidateModuleState,
  CandidateModuleStateTranslationKey,
} from "../../../model/examSessionCandidate/CandidateModuleState";
import { GetTranslation } from "../../../translations/GetTranslation";
import { ColumnDefinition, CustomGrid } from "../../common/customGrid/CustomGrid";
import PaymentStatus from "../../common/payment/PaymentStatus";
import { PaymentMethodType, PaymentState } from "../../payment/PaymentConstants";
import TranslatedMessage from "../../translations/TranslatedMessage";
import ExamSessionCandidateAddDialog from "./ExamSessionCandidateAddDialog";
import ExamSessionCandidateListHeader from "./ExamSessionCandidateListHeader";
import { SignupModuleRequestModel } from "../../../model/ExamSignup/SignupModuleRequestModel";
import { ExamType } from "../../../model/Exam";
import { ExamSessionCandidateExamState } from "../../../model/examSessionCandidate/ExamSessionCandidateExamViewModel";

interface RowData {
  examSessionCandidateId: string;
  candidateLastName: string;
  candidateFirstName: string;
  candidateEmail: string;
  moduleId: string;
  moduleName: string;
  documentsUploaded: boolean;
  moduleState: CandidateModuleState;
  paymentState: PaymentState;
  paymentMethod?: PaymentMethodType | null;
  refundStatus?: boolean;
  allRoomsAssigned: boolean;
}

interface ExamSessionCandidateListProps {
  isLife: boolean;
}

const ExamSessionCandidateList: FC<ExamSessionCandidateListProps> = ({isLife}) => {
  const history = useHistory();
  const intl = useIntl();
  const { examSessionId } = useParams<{ examSessionId: string }>();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const { data: examSessionCandidates, isFetching, refetch } = useGetExamSessionCandidatesQuery(examSessionId);
  const [signUp, { isLoading: isSingUpLoading }] = useSignupMutation();

  useEffect(() => {
    refetch();
  }, [examSessionId, refetch]);

  if (isFetching || isSingUpLoading) {
    return (
      <>
        <ExamSessionCandidateListHeader />
        <LinearProgress variant="query" />
      </>
    );
  }

  if (examSessionCandidates === undefined) {
    return <></>;
  }

  const closeAddDialog = () => setIsAddDialogOpen(false);

  const addExamSessionCandidate = async (candidateId: string, module: ModuleForSignupViewModel) => {
    setIsAddDialogOpen(false);
    const request: SignupModuleRequestModel = {
      courseId: module.courseId,
      examSessionId: examSessionId,
      moduleId: module.id,
      isRepeating: false,
      trainingProvider: "",
      willProvideDocumentsLater: module.needsDocuments,
      option: undefined,
      exams: [],
      bookingWithInvoice: module.bookingWithInvoice,
      isLife: isLife,
    };
    signUp({ candidateId, data: request });
  };

  const navigateToExamSessionCandidate = (row: RowData) => {
    const path = routesPaths.examSessionCandidate(row.examSessionCandidateId, row.moduleId);
    history.push(path);
  };

  const getRows = () => {
    const rows: RowData[] = [];
    examSessionCandidates.forEach((examSessionCandidate) => {
      examSessionCandidate.modules.forEach((module) => {

        const termPaperModuleExams = examSessionCandidate.exams.filter((e) =>
          e.moduleId === module.moduleId && ((e.type as ExamType) === ExamType.TermPaper));

        const noTermPaperModuleExams = examSessionCandidate.exams.filter((e) =>
          e.moduleId === module.moduleId && ((e.type as ExamType) !== ExamType.TermPaper));

        const allRoomsAssigned = (
          noTermPaperModuleExams.length > 0
          && module.state !== CandidateModuleState.Cancelled
          && noTermPaperModuleExams.filter(e => !e.roomId).length === 0
        )
        || (
          noTermPaperModuleExams.length === 0
          && termPaperModuleExams.length > 0
          && termPaperModuleExams.filter(e => e.state === ExamSessionCandidateExamState.Booked).length === termPaperModuleExams.length
        );

        rows.push({
          examSessionCandidateId: examSessionCandidate.id,
          candidateLastName: examSessionCandidate.lastName,
          candidateFirstName: examSessionCandidate.firstName,
          candidateEmail: examSessionCandidate.email,
          moduleId: module !== undefined ? module.moduleId : "",
          moduleName: module !== undefined ? GetTranslatedValueIntl(intl, module.name) : "",
          documentsUploaded: module.documentsUploaded,
          moduleState: module !== undefined ? module.state : CandidateModuleState.None,
          paymentState: module.paymentState,
          paymentMethod: module.paymentMethod,
          refundStatus: module.refundStatus,
          allRoomsAssigned,
        });
      });
    });
    return rows;
  };

  const rows: RowData[] = getRows();

  const columnsDefinitions: ColumnDefinition<RowData>[] = [
    {
      columnKey: "candidateLastName",
      columnLabel: <TranslatedMessage id="person.field.lastName" />,
      cellValue: (row) => row.candidateLastName,
    },
    {
      columnKey: "candidateFirstName",
      columnLabel: <TranslatedMessage id="person.field.firstName" />,
      cellValue: (row) => row.candidateFirstName,
    },
    {
      columnKey: "candidateEmail",
      columnLabel: <TranslatedMessage id="person.field.email" />,
      cellValue: (row) => row.candidateEmail,
    },
    {
      columnKey: "moduleName",
      columnLabel: <TranslatedMessage id="module.field.moduleName" />,
      cellValue: (row) => row.moduleName,
    },
    {
      columnKey: "documentsUploaded",
      columnLabel: <TranslatedMessage id="module.field.documentsUploaded" />,
      cellValue: (row) => (row.documentsUploaded ? <DoneIcon style={{ width: "50%" }} /> : ""),
      orderValue: (row) => row.documentsUploaded,
    },
    {
      columnKey: "allRoomsAssigned",
      columnLabel: <TranslatedMessage id="module.field.allRoomsAssigned" />,
      cellValue: (row) => (row.allRoomsAssigned ? <DoneIcon style={{ width: "50%" }} /> : <CloseIcon style={{ width: "50%", color: "red" }} />),
      orderValue: (row) => row.allRoomsAssigned,
    },
    {
      columnKey: "paymentState",
      columnLabel: <TranslatedMessage id="module.field.paymentState" />,
      cellValue: (row) => <PaymentStatus state={row.paymentState} method={row.paymentMethod} refunded={row.refundStatus} />,
      orderValue: (row) => row.paymentState,
    },
    {
      columnKey: "state",
      columnLabel: <TranslatedMessage id="certification.state" />,
      cellValue: (row) => {
        const translationKey = CandidateModuleStateTranslationKey[row.moduleState];
        return translationKey !== undefined ? GetTranslation(intl, translationKey) : "";
      },
    },
  ];

  return (
    <>
      <ExamSessionCandidateListHeader />
      <Grid container={true} spacing={2}>
        <Grid item={true} xs={12}>
          <CustomGrid<RowData>
            columnDefinitions={columnsDefinitions}
            rows={rows}
            onRowClick={navigateToExamSessionCandidate}
            rowKey={(row) => `${row.examSessionCandidateId}_${row.moduleId}`}
            addButtonTooltip={<TranslatedMessage id="examSessionCandidate.addCandidate" />}
            onAddClick={() => setIsAddDialogOpen(true)}
            rowsPerPage={5}
          />
        </Grid>
      </Grid>
      <ExamSessionCandidateAddDialog
        examSessionId={examSessionId}
        isOpen={isAddDialogOpen}
        add={addExamSessionCandidate}
        cancel={closeAddDialog}
      />
    </>
  );
};

export default ExamSessionCandidateList;
